import { AfterViewInit, Directive, ElementRef, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Prepends a screen-reader-only "Tooltip:" label to CDK aria-describedby messages
 * created by matTooltip, following the Deque University tooltip pattern.
 * The visible matTooltip text is unchanged.
 */
@Directive({
  selector: '[matTooltip][appAccessibleTooltip]'
})
export class AccessibleTooltipDirective implements AfterViewInit, OnDestroy {
  private hostObserver?: MutationObserver;
  private messageObservers = new Map<string, MutationObserver>();

  constructor(
    private el: ElementRef<HTMLElement>,
    private translate: TranslateService
  ) {}

  ngAfterViewInit(): void {
    this.hostObserver = new MutationObserver(() => this.syncDescribedByMessages());
    this.hostObserver.observe(this.el.nativeElement, {
      attributes: true,
      attributeFilter: ['aria-describedby']
    });

    this.el.nativeElement.addEventListener('focusin', this.syncDescribedByMessages);
    this.syncDescribedByMessages();
  }

  ngOnDestroy(): void {
    this.hostObserver?.disconnect();
    this.messageObservers.forEach(observer => observer.disconnect());
    this.messageObservers.clear();
    this.el.nativeElement.removeEventListener('focusin', this.syncDescribedByMessages);
  }

  private syncDescribedByMessages = (): void => {
    const describedBy = this.el.nativeElement.getAttribute('aria-describedby');
    if (!describedBy) {
      return;
    }

    describedBy.split(/\s+/).forEach(id => {
      if (!id.startsWith('cdk-describedby-message')) {
        return;
      }

      const messageEl = document.getElementById(id);
      if (!messageEl) {
        return;
      }

      if (!this.messageObservers.has(id)) {
        const observer = new MutationObserver(() => this.injectScreenReaderLabel(messageEl));
        observer.observe(messageEl, { childList: true, characterData: true, subtree: true });
        this.messageObservers.set(id, observer);
      }

      this.injectScreenReaderLabel(messageEl);
    });
  };

  private injectScreenReaderLabel(messageEl: HTMLElement): void {
    if (messageEl.querySelector('.tooltip-sr-label')) {
      return;
    }

    const text = messageEl.textContent?.trim();
    if (!text) {
      return;
    }

    const labelSpan = document.createElement('span');
    labelSpan.className = 'tooltip-sr-label cdk-visually-hidden';
    labelSpan.setAttribute('aria-label', this.translate.instant('aria-labels.tooltip_prefix').trim());
    labelSpan.setAttribute('role', 'tooltip');

    messageEl.textContent = '';
    messageEl.appendChild(labelSpan);
    messageEl.appendChild(document.createTextNode(text));
  }
}
